import { type ApiError, type BusinessErrorCode, normalizeError } from "./errors";

/** i18n key namespace for business error codes (`errors.codes.<CODE>`). */
const CODES_PREFIX = "errors.codes";

/**
 * Business error codes grouped by the product behavior they should trigger.
 * The UI branches on the *group* so individual codes don't each need bespoke
 * handling (see the auth pages for usage).
 */
export const ERROR_CODE_GROUPS = {
  /**
   * Soft session expiry — interceptor may single-flight refresh then retry.
   * Do not put SESSION_REVOKED here (force logout, never refresh).
   */
  AUTH_SESSION: ["UNAUTHORIZED", "HTTP_ERROR", "INVALID_REFRESH_TOKEN"],
  /** Hard session death — clear tokens and go to login; never refresh. */
  AUTH_REVOKED: ["SESSION_REVOKED", "INVALID_REFRESH_TOKEN"],
  /** Forced password change — keep tokens; route to /change-password. */
  PASSWORD_RESET: ["PASSWORD_RESET_REQUIRED"],
  /** Login-time errors shown inline on the login form. */
  LOGIN: ["INVALID_CREDENTIALS", "EMAIL_NOT_VERIFIED", "TEMPORARY_PASSWORD_EXPIRED"],
  /** Account state errors that block access entirely. */
  ACCOUNT_STATE: ["ACCOUNT_SUSPENDED", "ACCOUNT_DISABLED", "ACCOUNT_LOCKED", "NO_PORTAL_ACCESS"],
  /** OTP verification errors. */
  OTP: ["OTP_INVALID", "OTP_MAX_ATTEMPTS"],
  /** Throttling. */
  RATE_LIMIT: ["RATE_LIMITED"],
  /** Duplicate / conflict errors mapped to a form field where possible. */
  CONFLICT: [
    "EMAIL_ALREADY_EXISTS",
    "GEOGRAPHY_DUPLICATE_NAME",
    "CATALOG_DUPLICATE_CODE",
    "DELIVERY_SLOT_CODE_EXISTS",
    "READINESS_DUPLICATE_CODE",
    "READINESS_DUPLICATE_RULE_TYPE",
    "TRUCK_PLATE_EXISTS",
    "DRIVER_LICENSE_EXISTS",
    "CONTAINER_CODE_EXISTS",
    "ROLE_CODE_TAKEN",
    "ROLE_NAME_TAKEN",
    "DOCUMENT_TYPE_CODE_EXISTS",
    "UNIFIED_NUMBER_TAKEN",
  ],
  /** Field-level validation errors. */
  VALIDATION: ["VALIDATION_ERROR"],
  /**
   * The work order moved on (someone else started, completed or cancelled it)
   * — show the message, close the dialog and refetch instead of retrying.
   */
  WORK_ORDER_STALE: [
    "WORK_ORDER_NOT_ASSIGNABLE",
    "WORK_ORDER_NOT_ASSIGNED",
    "WORK_ORDER_NOT_IN_PROGRESS",
    "WORK_ORDER_CLOSED",
    "WORK_ORDER_CANCELLED",
  ],
} as const satisfies Record<string, readonly BusinessErrorCode[]>;

export type ErrorCodeGroup = keyof typeof ERROR_CODE_GROUPS;

/**
 * Map of business error code -> form field name, used to surface conflict /
 * duplicate errors inline on the relevant input instead of a generic toast.
 */
export const ERROR_CODE_FIELD_MAP: Partial<Record<BusinessErrorCode, string>> = {
  EMAIL_ALREADY_EXISTS: "email",
  GEOGRAPHY_DUPLICATE_NAME: "name",
  GEOGRAPHY_DUPLICATE_CODE: "code",
  GEOGRAPHY_DUPLICATE_ISO_CODE: "isoCode",
  GEOGRAPHY_DISTRICT_MISMATCH: "districtId",
  CATALOG_DUPLICATE_CODE: "code",
  DELIVERY_SLOT_CODE_EXISTS: "code",
  READINESS_DUPLICATE_CODE: "code",
  READINESS_DUPLICATE_RULE_TYPE: "ruleType",
  UNIFIED_NUMBER_TAKEN: "unifiedNumber",
  VENDOR_DOCUMENT_EXPIRED: "expiresAt",
  VENDOR_DOCUMENT_SCOPE_INVALID: "vendorServiceId",
  TRUCK_PLATE_EXISTS: "plateNumber",
  DRIVER_LICENSE_EXISTS: "licenseNumber",
  CONTAINER_CODE_EXISTS: "code",
  ROLE_CODE_TAKEN: "code",
  ROLE_CODE_REQUIRED: "code",
  ROLE_NAME_TAKEN: "name",
  INVALID_PLATFORM_ROLE: "roleIds",
  PERMISSION_NOT_ASSIGNABLE: "permissionCodes",
  PLATFORM_PERMISSION_NOT_ASSIGNABLE: "permissionCodes",
  PERMISSION_NOT_FOUND: "permissionCodes",
  DOCUMENT_TYPE_CODE_EXISTS: "code",
  LOCATION_COORDINATES_INCOMPLETE: "latitude",
  LOCATION_MAP_OR_COORDINATES: "mapUrl",
  REQUEST_OPTIONS_DUPLICATE: "options",
  REQUEST_OPTIONS_DUPLICATE_TYPE: "options",
  REQUEST_OPTION_SERVICE_MISMATCH: "options",
  REQUEST_OPTION_INACTIVE: "options",
  INVALID_SERVICE_DATE: "serviceStartDate",
  WALLET_ATTACHMENT_ALREADY_LINKED: "attachmentFileIds",
  CONTAINER_REQUIRED: "containerId",
  WORK_ORDER_PROOF_REQUIRED: "proofFileIds",
  WORK_ORDER_PROOF_LIMIT: "proofFileIds",
  WORK_ORDER_PROOF_ALREADY_LINKED: "proofFileIds",
  REQUEST_SITE_IMAGES_NOT_EDITABLE: "siteImageFileIds",
  REQUEST_SITE_IMAGE_ALREADY_LINKED: "siteImageFileIds",
};

/** Extract the backend {@link BusinessErrorCode} from any thrown value. */
export function getBusinessErrorCode(error: unknown): BusinessErrorCode | null {
  const apiError: ApiError = normalizeError(error);
  return apiError.errorCode ?? null;
}

/** Whether `code` belongs to the given {@link ErrorCodeGroup}. */
export function isErrorCodeInGroup(
  code: BusinessErrorCode | null | undefined,
  group: ErrorCodeGroup,
): boolean {
  if (!code) return false;
  return (ERROR_CODE_GROUPS[group] as readonly BusinessErrorCode[]).includes(code);
}

/** Resolve the form field associated with a business error code, if any. */
export function getFieldForErrorCode(code: BusinessErrorCode | null | undefined): string | null {
  if (!code) return null;
  return ERROR_CODE_FIELD_MAP[code] ?? null;
}

/**
 * Localize a business error code via `errors.codes.<CODE>`. Falls back to the
 * backend-provided `serverMessage`, then a generic message.
 */
export function getErrorCodeMessage(
  error: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const apiError = normalizeError(error);
  const code = apiError.errorCode;

  if (code) {
    const localized = t(`${CODES_PREFIX}.${code}`, {
      defaultValue: apiError.serverMessage ?? "",
    });
    if (localized) return localized;
  }

  if (apiError.serverMessage) return apiError.serverMessage;
  return t("errors.unknown");
}
